import { createSlice, PayloadAction } from "@reduxjs/toolkit"
import { ModalNames } from "features/modals/types"
import { OverlayModalType } from "themes/default-tailwind/modals/module"
import { madeBottomModalVisibleAction } from "./actions"
import { initialState, defaultBackgroundClose } from "./constants"
import { OverlayState, UserProfileContext, WorkspaceConfirmationContext } from "./types"

export const overlaySlice = createSlice({
    name: "overlay",
    initialState: { ...initialState },
    reducers: {
        setTitle: (state: OverlayState, action: PayloadAction<string>) => {
            state.title = action.payload
        },
        setDescription: (state: OverlayState, action: PayloadAction<string>) => {
            state.description = action.payload
        },
        setContentComponent: (state: OverlayState, action: PayloadAction<string | undefined>) => {
            state.contentComponentName = action.payload
        },
        setType: (state: OverlayState, action: PayloadAction<OverlayModalType>) => {
            state.type = action.payload
        },
        hiddenOverlay: (state: OverlayState) => {
            state.isVisible = false
            state.backgroundClose = defaultBackgroundClose
        },
        activatedOverlay: (state: OverlayState) => {
            state.isVisible = true
            state.backgroundClose = defaultBackgroundClose
        },
        toggleOverlay: (state: OverlayState) => {
            state.isVisible = !state.isVisible
        },
        queueComponent: (state: OverlayState, action: PayloadAction<string>) => {
            state.componentQueue.push(action.payload)
        },
        removedQueuedComponent: (
            state: OverlayState,
            action: PayloadAction<number | undefined>
        ) => {
            state.componentQueue.splice(action.payload ?? 0, 1)
        },
        enabledBackgroundClose: (state: OverlayState) => {
            state.backgroundClose = true
        },
        disabledBackgroundClose: (state: OverlayState) => {
            state.backgroundClose = false
        },
        resetOverlay: () => {
            return { ...initialState }
        },
        // reducers sets up everything to display a given component name in the modal of the center
        // to show it, the caller needs to switch the runtimelevel themselves
        initializeCenterModalForComponent: (
            state,
            action: PayloadAction<string | { componentName: string; componentType?: string }>
        ) => {
            if (typeof action.payload === "string") {
                state.contentComponentName = action.payload
                state.contentComponentType = undefined
            } else {
                state.contentComponentName = action.payload.componentName
                state.contentComponentType = action.payload.componentType
            }
            state.type = OverlayModalType.General
        },
        setWorkspaceConfirmationModalContext: (
            state,
            action: PayloadAction<WorkspaceConfirmationContext>
        ) => {
            state.workspaceConfirmationContext = action.payload
            state.contentComponentName = ModalNames.WorkspaceConfirmationModal
        },
        setUserProfileModalContext: (state, action: PayloadAction<UserProfileContext>) => {
            state.userProfileContext = action.payload
            state.contentComponentName = ModalNames.UserProfileModal
        },
    },
    extraReducers: (builder) => {
        builder.addCase(madeBottomModalVisibleAction, (state, action) => {
            state.type = OverlayModalType.Bottom
            state.contentComponentName = action.payload.componentName
        })
    },
})

export const {
    initializeCenterModalForComponent: initializeCenterModalForComponentAction,
    setTitle,
    setDescription,
    setContentComponent,
    setType,
    hiddenOverlay: hiddenOverlayAction,
    activatedOverlay: activatedOverlayAction,
    toggleOverlay,
    queueComponent,
    removedQueuedComponent,
    enabledBackgroundClose: enabledBackgroundCloseAction,
    disabledBackgroundClose: disabledBackgroundCloseAction,
    resetOverlay: resetOverlayAction,
    setWorkspaceConfirmationModalContext,
    setUserProfileModalContext,
} = overlaySlice.actions

export default overlaySlice.reducer
